import React, { useEffect, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import JobList from "./JobList";
import conf from "../../conf/conf";
import { FaWhatsapp, FaTelegramPlane } from "react-icons/fa";
import { getAllJobs, GetJobById } from "../../store/jobSlice";
import PageTitle from "../PageTitle";
import QuoteCard from "../../templates/QuoteCard";
import AdCard from "../../templates/AdCard";

function JobDetails() {
  const { id } = useParams();
  const moreJobsRef = useRef(null);
  const dispatch = useDispatch();

  const jobs = useSelector((state) => state.job.jobs);

  const whatsappLink = conf.whatsapp_url;
  const telegramLink = conf.telegram_url;

  const scrollToMoreJobs = () => {
    if (moreJobsRef.current) {
      const yOffset = -120;
      const y =
        moreJobsRef.current.getBoundingClientRect().top +
        window.pageYOffset +
        yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  useEffect(() => {
    window.addEventListener("scrollToMoreJobs", scrollToMoreJobs);
    return () =>
      window.removeEventListener("scrollToMoreJobs", scrollToMoreJobs);
  }, []);

  useEffect(() => {
    if (!jobs || jobs.length === 0) {
      dispatch(getAllJobs("Job"));
    }
  }, [dispatch, jobs]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);

  const currentJob = useMemo(() => {
    return jobs.find((job) => String(job.$id) === String(id));
  }, [jobs, id]);

  useEffect(() => {
    if (id) dispatch(GetJobById(id));
  }, [id, dispatch]);

  return (
    <div className="min-h-screen bg-gray-50 relative">
      <PageTitle
        title={
          currentJob
            ? `${currentJob.Title} | JobIntelPro`
            : `Job ${id} | JobIntelPro`
        }
      />

      {/* ===== Main Job Section ===== */}
      <div className="w-full max-w-6xl mx-auto px-4 pt-28 pb-10 flex flex-col lg:flex-row gap-5">
        {/* Job Details Section */}
        <div className="flex-1">
          <div className="p-6 sm:p-10 bg-gradient-to-r from-indigo-600 to-teal-500 rounded-3xl shadow-xl text-white">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-center mb-6">
              {currentJob ? currentJob.Title : "Page Not Found"}
            </h1>

            {currentJob?.Company && (
              <p className="text-center mb-4">
                <span className="text-xl sm:text-2xl font-semibold font-serif">
                  {currentJob.Company}
                </span>
              </p>
            )}

            {currentJob ? (
              <div className="bg-white text-gray-800 rounded-2xl p-6 sm:p-8 shadow-md text-left">
                <div className="space-y-4">
                  {currentJob.Description && (
                    <div>
                      <span className="font-bold text-lg text-indigo-700">
                        Job Description:
                      </span>
                      <div
                        className="prose max-w-none mt-1 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: currentJob.Description,
                        }}
                      />
                    </div>
                  )}

                  {(currentJob.City || currentJob.Country) && (
                    <p>
                      <span className="font-bold text-lg text-indigo-700">
                        Location:
                      </span>{" "}
                      {currentJob.City}{" "}
                      {currentJob.Country && `, ${currentJob.Country}`}
                    </p>
                  )}

                  {currentJob.WorkType && (
                    <p>
                      <span className="font-bold text-lg text-indigo-700">
                        Work Type:
                      </span>{" "}
                      {currentJob.WorkType}
                    </p>
                  )}

                  {currentJob.Experience && (
                    <div>
                      <span className="font-bold text-lg text-indigo-700">
                        Experience:
                      </span>
                      <div
                        className="prose max-w-none mt-1 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: currentJob.Experience,
                        }}
                      />
                    </div>
                  )}

                  {currentJob.Degree && (
                    <div>
                      <span className="font-bold text-lg text-indigo-700">
                        Qualifications:
                      </span>
                      <div
                        className="prose max-w-none mt-1 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: currentJob.Degree,
                        }}
                      />
                    </div>
                  )}

                  {currentJob.Skills && (
                    <div>
                      <span className="font-bold text-lg text-indigo-700 block mb-1">
                        Skills Required:
                      </span>
                      <div
                        className="prose max-w-none leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: currentJob.Skills }}
                      />
                    </div>
                  )}

                  <section className="max-w-6xl mx-auto px-4 mb-3">
                    <AdCard slot="9326257490" format="fluid" />
                  </section>

                  {currentJob.Responsibilities && (
                    <div>
                      <span className="font-bold text-lg text-indigo-700 block mb-1">
                        Responsibilities:
                      </span>
                      <div
                        className="prose max-w-none leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: currentJob.Responsibilities,
                        }}
                      />
                    </div>
                  )}

                  {currentJob.Salary && (
                    <p>
                      <span className="font-bold text-lg text-indigo-700">
                        Salary:
                      </span>{" "}
                      {currentJob.Salary}
                    </p>
                  )}

                  {currentJob.CompanyDescription && (
                    <div>
                      <span className="font-bold text-lg text-indigo-700">
                        Company Overview:
                      </span>
                      <div
                        className="prose max-w-none mt-1 leading-relaxed"
                        dangerouslySetInnerHTML={{
                          __html: currentJob.CompanyDescription,
                        }}
                      />
                    </div>
                  )}

                  {currentJob.ApplyURL && (
                    <div className="mt-8 text-center">
                      <a
                        href={currentJob.ApplyURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block bg-gradient-to-r from-indigo-600 to-teal-500 text-white font-bold px-8 py-3 rounded-xl shadow-lg hover:scale-105 hover:opacity-95 transition"
                      >
                        Apply Now
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-red-100 border-2 border-red-500 text-red-700 rounded-2xl p-6 sm:p-8 shadow-lg text-center flex flex-col items-center gap-4">
                <p className="text-3xl sm:text-4xl font-extrabold">
                  ❌ This page doesn't exist
                </p>
                <p className="text-lg sm:text-xl font-semibold">
                  Contact Admin for more details:
                </p>

                <div className="flex justify-center gap-4">
                  {whatsappLink && (
                    <a
                      href={whatsappLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-green-500 text-white px-4 py-2 rounded-full shadow-md hover:bg-green-600 transition"
                    >
                      <FaWhatsapp className="text-xl" />
                      WhatsApp
                    </a>
                  )}
                  {telegramLink && (
                    <a
                      href={telegramLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2 rounded-full shadow-md hover:bg-blue-600 transition"
                    >
                      <FaTelegramPlane className="text-xl" />
                      Telegram
                    </a>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quote Section */}
      <div className="mt-10 mb-8 px-4">
        <QuoteCard />
      </div>

      {/* More Jobs Section */}
      <div ref={moreJobsRef} className="w-full py-8 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-4 border-pink-500 inline-block pb-1">
            More Jobs
          </h2>

          <JobList
            jobs={jobs}
            excludeId={id}
            adFrequency={0}
            jobsPerPage={18}
            onPagination={() =>
              window.dispatchEvent(new Event("scrollToMoreJobs"))
            }
          />
        </div>
      </div>
    </div>
  );
}

export default JobDetails;
